/**
 * SparklineChart — мини-чарт без зависимостей. Чистый SVG.
 *
 * Использование:
 *   <SparklineChart values={[60,62,65,70,72,80]} color="var(--color-success)" height={28} />
 *   <SparklineChart values={weeks} stretch fill showLast={false} height={34} />
 *
 * `stretch` — тянуться на всю ширину родителя вместо фиксированных `width`
 * пикселей (для спарклайна внутри плитки, ширина которой задана гридом).
 * Толщина линии при растяжении не искажается: viewBox остаётся в условных
 * координатах, а штрих рисуется non-scaling.
 * `fill` — мягкая заливка под линией: на маленькой высоте одна волосяная линия
 * теряется, заливка возвращает ряду вес.
 */

export default function SparklineChart({
    values = [],
    color = 'var(--color-success)',
    height = 28,
    width = 120,
    showLast = true,
    stretch = false,
    fill = false,
}) {
    if (!Array.isArray(values) || values.length === 0) {
        return (
            <span className="text-rel-xxs text-muted" style={{ height, display: 'inline-block' }}>
                нет данных
            </span>
        )
    }
    const max = Math.max(...values, 1)
    const min = Math.min(...values, 0)
    const range = max - min || 1
    const stepX = width / Math.max(1, values.length - 1)

    const points = values
        .map((v, i) => {
            const x = i * stepX
            const y = height - ((v - min) / range) * (height - 2) - 1
            return `${x.toFixed(1)},${y.toFixed(1)}`
        })
        .join(' ')

    const last = values[values.length - 1]
    const lastY = height - ((last - min) / range) * (height - 2) - 1

    return (
        <span style={{ display: stretch ? 'flex' : 'inline-flex', alignItems: 'center', gap: 8 }}>
            <svg
                width={stretch ? undefined : width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio={stretch ? 'none' : undefined}
                role="img"
                aria-label={`Тренд: ${values.length} точек, последнее значение ${last}`}
                style={stretch
                    ? { display: 'block', width: '100%', height }
                    : { overflow: 'visible' }}
            >
                {fill && (
                    <polygon points={`0,${height} ${points} ${width},${height}`} fill={color} opacity="0.12" />
                )}
                <polyline
                    fill="none"
                    stroke={color}
                    strokeWidth="1.5"
                    points={points}
                    strokeLinejoin={stretch ? 'round' : undefined}
                    strokeLinecap={stretch ? 'round' : undefined}
                    vectorEffect={stretch ? 'non-scaling-stroke' : undefined}
                />
                {!stretch && values.length > 0 && (
                    <circle cx={(values.length - 1) * stepX} cy={lastY} r={2.5} fill={color} />
                )}
            </svg>
            {showLast && (
                <span className="text-rel-xs fw-700" style={{ color }}>
                    {Math.round(last)}
                </span>
            )}
        </span>
    )
}
